/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Box, Typography, TextField, Button, Paper, Stack, Divider,
  CircularProgress, Alert, InputAdornment, ToggleButton, ToggleButtonGroup,
  Accordion, AccordionSummary, AccordionDetails, Slider, Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { useBranding, DEFAULT_AUTH_LAYOUT } from '../../contexts/BrandingContext';
import AuthBrandHeader from '../auth/AuthBrandHeader';
import { assessBrandColor, applyBrandTokens, isValidHex, DEFAULT_PRIMARY } from '../../utils/brandColors';
import '../../styles/BrandingManagement.css';

/**
 * Branding, in two independent halves:
 *
 *   1. Theme colour — applies to the whole app the instant you change it, so
 *      the admin sees the real thing rather than a swatch. It has its own Save
 *      button, and reverts to the saved colour if they leave without saving.
 *
 *   2. The sign-in page designer — the logo and heading are arranged directly on
 *      a full-size picture of the page: drag them where they should go, drag a
 *      corner to resize, type the heading in place. The canvas renders the very
 *      same component the real sign-in page uses, so what is arranged here is
 *      what visitors get, rather than an approximation drawn alongside a column
 *      of text fields.
 *
 * The remaining wording (names, subheadings, support email) is ordinary text and
 * sits in a section below, out of the way until it is wanted.
 */

/* Anything the browser can decode. Large photographs are shrunk in the browser
   before upload rather than refused. */
const UPLOAD_MAX_EDGE = 1400;      // px — plenty for a logo on a 4K display
const RECOMPRESS_ABOVE = 600 * 1024;

const prettyBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Shrink an oversized image in the browser so any file a person happens to have
 * — a phone photo, a poster export — can be used as a logo without them having
 * to resize it first. SVGs pass through untouched; they are already tiny and
 * scaling them would throw away their sharpness.
 *
 * Returns the original file if anything goes wrong: an upload that works is
 * worth more than one that is perfectly optimised.
 */
const prepareImage = async (file) => {
  if (file.type === 'image/svg+xml') return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // a format this browser can't decode; let the server judge it
  }

  const longest = Math.max(bitmap.width, bitmap.height);
  const scale = Math.min(1, UPLOAD_MAX_EDGE / longest);
  if (scale === 1 && file.size <= RECOMPRESS_ABOVE) {
    bitmap.close?.();
    return file;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();

  // PNG keeps transparency, which nearly every logo relies on.
  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  if (!blob) return file;

  const name = file.name.replace(/\.[^.]+$/, '') + '.png';
  return new File([blob], name, { type: 'image/png' });
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/* ── the designer canvas ─────────────────────────────────────────────────── */

/**
 * A full-size sign-in page with the logo and heading made draggable.
 *
 * Pointer events (not mouse events) so a trackpad, a touchscreen and a pen all
 * work. Each piece is also a focus target: arrow keys nudge it, so the layout
 * can be set without a mouse at all.
 */
const AuthDesigner = ({
  layout, onLayoutChange, heading, onHeadingChange, logoSrc, logoAlt, selected, onSelect
}) => {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const beginDrag = (part, mode) => (e) => {
    // Let a click land in the heading's text box without starting a drag.
    if (mode === 'move' && e.target.tagName === 'TEXTAREA') return;
    e.preventDefault();
    e.stopPropagation();
    onSelect(part);

    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = {
      part,
      mode,
      rect,
      startX: e.clientX,
      startY: e.clientY,
      origin: { ...layout[part] }
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dxPercent = ((e.clientX - drag.startX) / drag.rect.width) * 100;
    const dyPercent = ((e.clientY - drag.startY) / drag.rect.height) * 100;

    if (drag.mode === 'move') {
      onLayoutChange({
        ...layout,
        [drag.part]: {
          ...layout[drag.part],
          // A little overhang is allowed, so a logo can bleed off the edge on
          // purpose, but not so much that it can be lost off-canvas.
          x: clamp(drag.origin.x + dxPercent, -15, 105),
          y: clamp(drag.origin.y + dyPercent, -15, 105)
        }
      });
    } else {
      onLayoutChange({
        ...layout,
        [drag.part]: {
          ...layout[drag.part],
          width: clamp(drag.origin.width + dxPercent, 4, 100)
        }
      });
    }
  };

  const endDrag = (e) => {
    if (dragRef.current) {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      dragRef.current = null;
    }
  };

  const nudge = (part) => (e) => {
    const step = e.shiftKey ? 2 : 0.5;
    const moves = {
      ArrowLeft: { x: -step }, ArrowRight: { x: step },
      ArrowUp: { y: -step }, ArrowDown: { y: step }
    };
    if (moves[e.key]) {
      e.preventDefault();
      const move = moves[e.key];
      onLayoutChange({
        ...layout,
        [part]: {
          ...layout[part],
          x: clamp(layout[part].x + (move.x || 0), -15, 105),
          y: clamp(layout[part].y + (move.y || 0), -15, 105)
        }
      });
    } else if (e.key === '+' || e.key === '=' || e.key === '-') {
      e.preventDefault();
      const delta = e.key === '-' ? -2 : 2;
      onLayoutChange({
        ...layout,
        [part]: { ...layout[part], width: clamp(layout[part].width + delta, 4, 100) }
      });
    }
  };

  const partProps = (part, label) => ({
    className: `designer-part ${selected === part ? 'is-selected' : ''}`,
    tabIndex: 0,
    role: 'button',
    'aria-label': `${label}. Drag to move, or use the arrow keys. Plus and minus resize it.`,
    onPointerDown: beginDrag(part, 'move'),
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onKeyDown: nudge(part),
    onFocus: () => onSelect(part)
  });

  return (
    <div className="designer-canvas-wrap">
      <div className="designer-canvas" ref={canvasRef} onPointerDown={() => onSelect(null)}>
        <AuthBrandHeader
          layout={layout}
          heading={heading || 'Your heading goes here'}
          logoSrc={logoSrc}
          logoAlt={logoAlt}
          logoProps={partProps('logo', 'Logo')}
          headingProps={partProps('heading', 'Heading')}
          headingContent={
            // Editing happens in place: the text is typed where it will appear
            // rather than in a field somewhere else on the page.
            <textarea
              className="designer-heading-input"
              value={heading}
              onChange={(e) => onHeadingChange(e.target.value)}
              rows={2}
              aria-label="Heading text"
              placeholder="Your heading goes here"
              style={{ textAlign: layout.heading_align, fontSize: `${layout.heading_size}cqw` }}
            />
          }
        />

        {/* Resize grips, drawn over the selected piece. */}
        {['logo', 'heading'].map(part => (
          selected === part && (
            <button
              key={part}
              type="button"
              className="designer-resize"
              aria-label={`Resize ${part}`}
              style={{
                left: `calc(${layout[part].x + layout[part].width}% - 9px)`,
                top: `${layout[part].y}%`
              }}
              onPointerDown={beginDrag(part, 'resize')}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onKeyDown={nudge(part)}
            />
          )
        ))}

        {/* A faint picture of the form below, so the header is judged in
            proportion to the page it sits on. */}
        <div className="designer-form-ghost" aria-hidden="true">
          <div className="designer-ghost-button" />
          <div className="designer-ghost-field" />
          <div className="designer-ghost-field" />
        </div>
      </div>
    </div>
  );
};

/* ── page ───────────────────────────────────────────────────────────────── */

const BrandingManagement = () => {
  const { branding, refreshBranding, logoUrl, logoAlt } = useBranding();

  const [color, setColor] = useState(DEFAULT_PRIMARY);
  const [savedColor, setSavedColor] = useState(DEFAULT_PRIMARY);
  const [savingColor, setSavingColor] = useState(false);

  const [form, setForm] = useState(null);
  const [layout, setLayout] = useState(DEFAULT_AUTH_LAYOUT);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [page, setPage] = useState('signin');   // which page's heading is being edited
  const [selected, setSelected] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [logoError, setLogoError] = useState(null);
  const [localLogoPreview, setLocalLogoPreview] = useState(null);

  useEffect(() => {
    if (!branding) return;
    setColor(branding.primary_color || DEFAULT_PRIMARY);
    setSavedColor(branding.primary_color || DEFAULT_PRIMARY);
    const next = {
      product_name: branding.product_name || '',
      short_name: branding.short_name || '',
      organization_name: branding.organization_name || '',
      signin_heading: branding.signin_heading || '',
      signin_subheading: branding.signin_subheading || '',
      signup_heading: branding.signup_heading || '',
      signup_subheading: branding.signup_subheading || '',
      sso_button_label: branding.sso_button_label || '',
      support_email: branding.support_email || '',
      footer_text: branding.footer_text || '',
      logo_alt_text: branding.logo_alt_text || ''
    };
    setForm(next);
    setLayout(branding.auth_layout || DEFAULT_AUTH_LAYOUT);
    setSavedSnapshot(JSON.stringify({ form: next, layout: branding.auth_layout || DEFAULT_AUTH_LAYOUT }));
  }, [branding]);

  // Paint the chosen colour across the WHOLE application as it changes, so the
  // admin judges it against real navigation, buttons and tables rather than a
  // swatch. Nothing is persisted until they press Save.
  useEffect(() => {
    if (isValidHex(color)) applyBrandTokens(color);
  }, [color]);

  // Leaving without saving must not strand everyone on an unsaved colour.
  const savedColorRef = useRef(savedColor);
  savedColorRef.current = savedColor;
  useEffect(() => () => applyBrandTokens(savedColorRef.current), []);

  const assessment = useMemo(() => assessBrandColor(color), [color]);
  const colorDirty = color.toLowerCase() !== savedColor.toLowerCase();
  const designDirty = savedSnapshot !== null
    && savedSnapshot !== JSON.stringify({ form, layout });

  const handleSaveColor = async () => {
    if (!isValidHex(color)) {
      toast.error('Enter a valid hex colour, such as #c41e3a.');
      return;
    }
    setSavingColor(true);
    try {
      await axios.put('/api/branding', { primary_color: color });
      await refreshBranding();
      setSavedColor(color);
      toast.success('Theme colour saved. Everyone sees it from their next page load.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the theme colour.');
    } finally {
      setSavingColor(false);
    }
  };

  const handleRevertColor = () => {
    setColor(savedColor);
    applyBrandTokens(savedColor);
  };

  const change = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const headingField = page === 'signin' ? 'signin_heading' : 'signup_heading';

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    try {
      await axios.put('/api/branding', { ...form, auth_layout: layout });
      await refreshBranding();
      setSavedSnapshot(JSON.stringify({ form, layout }));
      toast.success('Sign-in page saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save.');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleDiscard = () => {
    if (!savedSnapshot) return;
    const { form: savedForm, layout: savedLayout } = JSON.parse(savedSnapshot);
    setForm(savedForm);
    setLayout(savedLayout);
  };

  const handleLogoPick = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';                       // re-picking the same file still fires
    if (!file) return;
    setLogoError(null);

    if (!file.type.startsWith('image/')) {
      setLogoError(`"${file.name}" is not an image file.`);
      return;
    }

    setUploading(true);
    const preview = URL.createObjectURL(file);
    setLocalLogoPreview(preview);

    try {
      const prepared = await prepareImage(file);
      const data = new FormData();
      data.append('image', prepared);
      // A description is no longer a gate on uploading — it defaults to
      // something sensible and can be improved afterwards.
      data.append('alt_text', (form?.logo_alt_text || '').trim());

      await axios.post('/api/branding/logo', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshBranding();
      setLocalLogoPreview(null);
      URL.revokeObjectURL(preview);
      toast.success(
        prepared === file
          ? 'Logo updated.'
          : `Logo updated. The image was ${prettyBytes(file.size)}, so it was resized for the web.`
      );
    } catch (err) {
      setLocalLogoPreview(null);
      URL.revokeObjectURL(preview);
      setLogoError(err.response?.data?.error || 'The upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [form, refreshBranding]);

  const handleLogoRemove = async () => {
    try {
      await axios.delete('/api/branding/logo');
      await refreshBranding();
      toast.success('Logo removed. The built-in logo is back.');
    } catch (err) {
      setLogoError(err.response?.data?.error || 'Could not remove the logo.');
    }
  };

  if (!form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress aria-label="Loading branding settings" />
      </Box>
    );
  }

  const severity = assessment?.level === 'warning' ? 'warning'
    : assessment?.level === 'notice' ? 'info' : 'success';
  const previewLogo = localLogoPreview || logoUrl || undefined;
  const selectedPart = selected ? layout[selected] : null;

  return (
    <Box className="branding-page">
      <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Branding
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '72ch' }}>
        The theme colour, which you can try out live before committing to it, and the sign-in page
        itself, which you arrange directly.
      </Typography>

      {/* ════ 1. THEME COLOUR ═══════════════════════════════════════════ */}
      <Paper variant="outlined" className="branding-card branding-theme-card">
        <div className="branding-card-head">
          <div>
            <Typography variant="h6" component="h2" className="branding-card-title">
              Theme colour
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '64ch' }}>
              Changing this re-colours the app around you straight away, so you can see it in
              context. Nothing is saved for anyone else until you press Save theme colour.
            </Typography>
          </div>
          {colorDirty && <span className="branding-unsaved">Unsaved preview</span>}
        </div>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" sx={{ mt: 2.5 }}>
          <TextField
            label="Hex colour"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            error={!isValidHex(color)}
            helperText={!isValidHex(color) ? 'Must be a hex code, e.g. #c41e3a' : ' '}
            sx={{ width: 210 }}
            inputProps={{ 'aria-describedby': 'brand-contrast-report' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <span
                    className="branding-swatch"
                    style={{ background: isValidHex(color) ? color : 'transparent' }}
                    aria-hidden="true"
                  />
                </InputAdornment>
              )
            }}
          />
          <Box>
            <label htmlFor="brand-color-picker" className="branding-label">Or pick visually</label>
            <input
              id="brand-color-picker"
              type="color"
              className="branding-color-input"
              value={isValidHex(color) ? color : DEFAULT_PRIMARY}
              onChange={(e) => setColor(e.target.value)}
            />
          </Box>
        </Stack>

        {assessment?.valid && (
          <Alert severity={severity} sx={{ mt: 1 }} id="brand-contrast-report">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Contrast check</Typography>
            {assessment.notes.map((note, i) => (
              <Typography key={i} variant="body2" component="p" sx={{ mb: 0.25 }}>{note}</Typography>
            ))}
          </Alert>
        )}

        <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            onClick={handleSaveColor}
            disabled={savingColor || !isValidHex(color) || !colorDirty}
          >
            {savingColor ? 'Saving…' : 'Save theme colour'}
          </Button>
          <Button variant="outlined" onClick={handleRevertColor} disabled={!colorDirty}>
            Revert to saved
          </Button>
        </Stack>
      </Paper>

      <Divider className="branding-divider" />

      {/* ════ 2. SIGN-IN PAGE DESIGNER ══════════════════════════════════ */}
      <div className="branding-card-head">
        <div>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Sign-in and sign-up pages
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '72ch' }}>
            Drag the logo and the heading where you want them, drag the square handle to resize,
            and type the heading straight onto the page. This is the real page, at full width —
            what you arrange here is what visitors see.
          </Typography>
        </div>
        {designDirty && <span className="branding-unsaved">Unsaved changes</span>}
      </div>

      <Paper variant="outlined" className="branding-card designer-card">
        <div className="designer-toolbar">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={page}
            onChange={(e, value) => value && setPage(value)}
            aria-label="Which page's heading to edit"
          >
            <ToggleButton value="signin">Sign-in page</ToggleButton>
            <ToggleButton value="signup">Sign-up page</ToggleButton>
          </ToggleButtonGroup>

          <div className="designer-toolbar-spacer" />

          <Button variant="contained" component="label" disabled={uploading} size="small">
            {uploading ? 'Uploading…' : logoUrl ? 'Replace logo' : 'Upload logo'}
            <input type="file" hidden accept="image/*" onChange={handleLogoPick} />
          </Button>
          {logoUrl && (
            <Button variant="outlined" size="small" onClick={handleLogoRemove}>
              Remove logo
            </Button>
          )}
          <Tooltip title="Put the logo and heading back where they started">
            <Button
              variant="outlined"
              size="small"
              onClick={() => setLayout(DEFAULT_AUTH_LAYOUT)}
            >
              Reset layout
            </Button>
          </Tooltip>
        </div>

        {logoError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLogoError(null)}>
            {logoError}
          </Alert>
        )}

        <AuthDesigner
          layout={layout}
          onLayoutChange={setLayout}
          heading={form[headingField]}
          onHeadingChange={(value) => setForm(prev => ({ ...prev, [headingField]: value }))}
          logoSrc={previewLogo}
          logoAlt={form.logo_alt_text || logoAlt}
          selected={selected}
          onSelect={setSelected}
        />

        {/* Size controls double as the mouse-free way to do what the corner grip
            does, and show the exact numbers. */}
        <div className="designer-controls">
          <div className="designer-control">
            <label className="branding-label" htmlFor="logo-size">
              Logo size — {Math.round(layout.logo.width)}% of the page width
            </label>
            <Slider
              id="logo-size"
              size="small"
              min={4}
              max={100}
              value={layout.logo.width}
              onChange={(e, value) => setLayout(prev => ({ ...prev, logo: { ...prev.logo, width: value } }))}
              aria-label="Logo size"
            />
          </div>

          <div className="designer-control">
            <label className="branding-label" htmlFor="heading-size">
              Heading size
            </label>
            <Slider
              id="heading-size"
              size="small"
              min={0.8}
              max={8}
              step={0.1}
              value={layout.heading_size}
              onChange={(e, value) => setLayout(prev => ({ ...prev, heading_size: value }))}
              aria-label="Heading size"
            />
          </div>

          <div className="designer-control designer-control-align">
            <span className="branding-label">Heading alignment</span>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={layout.heading_align}
              onChange={(e, value) => value && setLayout(prev => ({ ...prev, heading_align: value }))}
              aria-label="Heading alignment"
            >
              <ToggleButton value="left" aria-label="Align left"><FormatAlignLeftIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="center" aria-label="Align centre"><FormatAlignCenterIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="right" aria-label="Align right"><FormatAlignRightIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>

        <Typography variant="caption" color="text.secondary" className="designer-hint">
          {selectedPart
            ? `Selected: ${selected}. Arrow keys nudge it, plus and minus resize it.`
            : 'Tip: click the logo or the heading to select it. On a phone-sized screen the two stack automatically, whatever the arrangement here.'}
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          <Button variant="contained" onClick={handleSaveDetails} disabled={savingDetails || !designDirty}>
            {savingDetails ? 'Saving…' : 'Save sign-in page'}
          </Button>
          <Button variant="outlined" onClick={handleDiscard} disabled={!designDirty}>
            Discard changes
          </Button>
        </Stack>
      </Paper>

      {/* ════ 3. EVERYTHING ELSE ════════════════════════════════════════ */}
      <Accordion variant="outlined" className="branding-card branding-more" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
            Names, wording and support details
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2.5} sx={{ maxWidth: 620 }}>
            <TextField label="Product name" value={form.product_name} onChange={change('product_name')}
              helperText="Browser tab title and email headers." fullWidth />
            <TextField label="Short name" value={form.short_name} onChange={change('short_name')}
              helperText="Used where space is tight, such as the navigation bar." fullWidth />
            <TextField label="Organization" value={form.organization_name} onChange={change('organization_name')}
              helperText="Also used as the author on generated invoice PDFs." fullWidth />
            <Divider />
            <TextField label="Sign-in subheading" value={form.signin_subheading} onChange={change('signin_subheading')}
              helperText="Optional line under the heading." fullWidth multiline minRows={2} />
            <TextField label="Sign-up subheading" value={form.signup_subheading} onChange={change('signup_subheading')}
              fullWidth multiline minRows={2} />
            <TextField label="Single sign-on button label" value={form.sso_button_label} onChange={change('sso_button_label')}
              helperText='e.g. "Sign in with your NetID"' fullWidth />
            <TextField label="Support email" type="email" value={form.support_email} onChange={change('support_email')} fullWidth />
            <TextField label="Footer text" value={form.footer_text} onChange={change('footer_text')} fullWidth />
            <Divider />
            <TextField
              label="Logo description"
              value={form.logo_alt_text}
              onChange={change('logo_alt_text')}
              placeholder={`e.g. ${form.organization_name || 'University'} logo`}
              helperText="Read aloud in place of the logo by screen readers. Saved with the button below."
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleSaveDetails}
              disabled={savingDetails || !designDirty}
              sx={{ alignSelf: 'flex-start' }}
            >
              {savingDetails ? 'Saving…' : 'Save these details'}
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default BrandingManagement;
